import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Autocomplete,Card,CardContent,IconButton,InputAdornment,Pagination,Skeleton,Stack,TextField,Tooltip,Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext,useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { BsEye } from "react-icons/bs";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import PlanoDeAcao from "../../service/PlanoDeAcao";
import { analisesFinalizadasType,finalizadosType } from "../../types/plano-de-acao/types";
import ViewPlansById from "./modal/PlanoPorId";

const planosService = new PlanoDeAcao();

export default function Finalizadas() {
  const { userData } = useContext(UserContext);
  const { t, i18n } = useTranslation('translation');
  const [paginated, setPaginated] = useState<finalizadosType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [planIds, setPlanIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  function handleOpen() {
    setOpen(!open);
  }

  const lastPageIndex = currentPage * itemsPerPage;
  const firstPageIndex = lastPageIndex - itemsPerPage;

  const {
    data, isLoading, isFetching, isSuccess, isError } = useQuery({
      queryKey: ['GET_ANALISES_FINALIZADAS'],
      queryFn: async () => {
        toast.info('Buscando planos finalizados', { toastId: 'buscando1' });
        const token = await AttToken();
        if (token) {
          const response: analisesFinalizadasType = await planosService.getFinishedList(token);
          return response;
        }
      },
      enabled: userData.listaFuncionalidades.includes('plano-de-acao'),
      retry: false,
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    if (data && isSuccess) {
      toast.success(`${data?.analisesFinalizadas?.length} Planos finalizados`, { toastId: 'successoFinalizado' });
    } else {
      toast.success(`Nenhum plano finalizado`, { toastId: 'successoFinalizado' });
    }
  }, [data, isSuccess, t]);

  useEffect(() => {
    if (isSuccess && !isFetching && !isLoading && data?.analisesFinalizadas) {
      const currentPageData = data?.analisesFinalizadas.filter((ele) => ele.nomeForm.includes(search))
        ?.sort((a, b) => { return Number(a.dataCriacao) < Number(b.dataCriacao) ? 1 : -1; });
      setPaginated(currentPageData?.slice(firstPageIndex, lastPageIndex) ?? []);
    } if (isError) {
      toast.error('Erro ao buscar planos finalizados', { toastId: 'erro' });
    }
  }, [firstPageIndex, lastPageIndex, data, isSuccess, isFetching, isLoading, isError, t, search]);

  const pages = [];

  if (data?.total) {
    for (let i = 1; i <= Math.ceil(data?.analisesFinalizadas?.filter((ele) => ele?.nomeForm?.includes(search))?.length / itemsPerPage); i++) {
      pages.push(i);
    }
  }

  function Loading() {
    return (
      <>
        <Skeleton height={30} width='300px' variant="rounded" />
        {Array(5).fill(0).map((_, index) => (
          <Skeleton key={index} height={150} width='100%' variant="rounded" />
        ))}
      </>
    );
  };

  if (!data
    && !isLoading
    && !isFetching
  ) return (
    <span style={{
      width: '100%',
      display: 'flex',
      justifyContent: "center",
      color: 'gray',
      fontSize: '1.5em',
      fontWeight: 600
    }}>
      {t('ActionPlan.ListaAnalises.noAnalysesOrFailedPlans')}
    </span>);

  return (
    <>
      {
        open && (
          <ViewPlansById
            handleOpen={handleOpen}
            open={open}
            planIds={planIds as string[]}
            setPlanIds={setPlanIds}
          />
        )
      }
      <Stack
        direction='column'
        width='100%'
        justifyContent='flex-start'
        alignItems='flex-start'
        spacing={2}
      >
        {isLoading || isFetching
          ? <Loading />
          : data &&
          <>
            <Stack
              justifyContent='space-between'
              alignItems='center'
              width='100%'
              direction="row"
              spacing={1}
            >
              <Typography width={data?.total < 5 ? '100%' : 'auto'} textAlign='end' variant='h6' sx={{ color: '#47AA9C' }}>
                {`${data?.analisesFinalizadas?.length ? data.analisesFinalizadas?.length : 'Nenhum'} ${data?.analisesFinalizadas?.length > 1
                  ? t('ActionPlan.ListaAnalises.completedAnalysesLabel').slice(0, -1)
                  : t('ActionPlan.ListaAnalises.completedAnalysisLabel')}`}
              </Typography>
              {data?.total > 5
                && <Pagination
                  style={{ display: `${!pages?.length ? 'none' : ''}` }}
                  boundaryCount={4}
                  showFirstButton
                  showLastButton
                  shape="rounded"
                  page={currentPage}
                  count={pages?.length}
                  onChange={(eve, pageNumber) => {
                    trackEventMatomo('lista analise de PA', 'click', 'button', 'muda página');
                    setCurrentPage(pageNumber);
                  }}
                />}
            </Stack>
            {data?.total > 5
              && <Autocomplete
                fullWidth
                value={search ? ({ nomeForm: search } as finalizadosType) : null}
                getOptionLabel={option => option.nomeForm as string}
                getOptionKey={option => option.id}
                isOptionEqualToValue={(option, value) => option.nomeForm === value.nomeForm}
                options={data?.analisesFinalizadas
                  ?.sort((a, b) => { return a.nomeForm?.toLowerCase() > b.nomeForm?.toLowerCase() ? 1 : - 1; })
                  ?.map((ele: finalizadosType) => ({ ...ele })) || []}
                onChange={(_eve, value: finalizadosType | null) => {
                  if (value?.nomeForm) {
                    setSearch(value?.nomeForm as string);
                    setCurrentPage(1);
                  }
                  else {
                    setSearch("");
                  }
                }}
                renderInput={(params) => <TextField {...params} label={t('ActionPlan.PlanosDeAcaoComp.searchPlanByName')} />}
              />}
            {paginated?.sort((a, b) => b?.dataCriacao - a?.dataCriacao)?.map((analise, index: number) =>
              <Card key={index} sx={{ width: '100%' }}>
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography variant='h5' fontWeight={600} sx={{ color: '#47AA9C' }}>
                      {analise?.nomeForm}
                    </Typography>
                    <Stack
                      direction="column"
                      spacing={1}
                      sx={{
                        justifyContent: "center",
                        alignItems: "flex-start",
                      }}
                    >
                      <TextField
                        disabled
                        variant='standard'
                        value={analise.userFinalizadorNome}
                        label={t('ActionPlan.ListaAnalises.analyzedBy')}
                      />
                      {analise.rejeitada
                        && <span className='tag is-danger'>
                          {t('ActionPlan.ListaAnalises.analysisRejected')}
                        </span>}
                      {analise.planosCriados
                        && analise.planosCriados.length
                        && (<Stack direction='row' justifyContent='space-between' alignItems='center' width='100%'>
                          <span className='tag is-success'>
                            {`${analise.planosCriados.length > 1
                              ? `${analise.planosCriados.length} ${t('ActionPlan.ListaAnalises.actionPlansCreated')}`
                              : `${t('ActionPlan.ListaAnalises.oneActionPlanCreated')}`}`}
                          </span>
                          <Tooltip title={t('ActionPlan.ListaAnalises.viewOpenPlans')}>
                            <IconButton onClick={() => {
                              setOpen(true);
                              trackEventMatomo('lista analise de PA', 'click', 'button', 'abre modal');
                              setPlanIds(analise?.planosCriados || []);
                            }}>
                              <BsEye />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        )}
                      <TextField
                        inputProps={{
                          style: { textAlign: 'center' },
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarMonthIcon />
                              </InputAdornment>
                            ),
                          },
                        }}
                        disabled
                        variant='outlined'
                        label={t('ActionPlan.ListaAnalises.formCompletionDate')}
                        value={new Date(analise.dataCriacao || 0).toLocaleString(i18n.language === 'en' ? 'en-US' : 'pt-BR')}
                      />
                      <TextField
                        inputProps={{
                          style: { textAlign: 'center' },
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarMonthIcon />
                              </InputAdornment>
                            ),
                          },
                        }}
                        disabled
                        variant='outlined'
                        label={'Data de análise'}
                        value={new Date(analise.dataAnalise || 0).toLocaleString(i18n.language === 'en' ? 'en-US' : 'pt-BR')}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </>
        }
      </Stack>
    </>
  );
}
